const DATABASE_NAME = 'boozeon-offline-cache';
const DATABASE_VERSION = 1;
const STORE_NAME = 'apollo-cache';
const CACHE_VERSION = 1;
const MAX_CACHE_BYTES = 5 * 1024 * 1024;
const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;

let activeUserId = null;
let persistTimer = null;

const serializedByteLength = value => {
    if (typeof Blob !== 'undefined') {
        return new Blob([value]).size;
    }
    if (typeof TextEncoder !== 'undefined') {
        return new TextEncoder().encode(value).byteLength;
    }
    // Conservative fallback for older test/browser runtimes.
    return value.length * 2;
};

const canUseIndexedDb = () => (
    typeof window !== 'undefined' &&
    typeof window.indexedDB !== 'undefined'
);

const openDatabase = () => new Promise((resolve, reject) => {
    if (!canUseIndexedDb()) {
        resolve(null);
        return;
    }

    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE_NAME)) {
            database.createObjectStore(STORE_NAME);
        }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Offline cache database is blocked'));
});

const runTransaction = async (mode, action) => {
    const database = await openDatabase();
    if (!database) return null;

    try {
        return await new Promise((resolve, reject) => {
            const transaction = database.transaction(STORE_NAME, mode);
            const store = transaction.objectStore(STORE_NAME);
            const request = action(store);
            let result;
            request.onsuccess = () => {
                result = request.result;
            };
            request.onerror = () => reject(request.error);
            transaction.oncomplete = () => resolve(result);
            transaction.onabort = () => reject(transaction.error);
            transaction.onerror = () => reject(transaction.error);
        });
    } finally {
        database.close();
    }
};

const storageKey = userId => `user:${userId}`;

const readCache = userId => runTransaction(
    'readonly',
    store => store.get(storageKey(userId))
);

const writeCache = (userId, data) => runTransaction(
    'readwrite',
    store => store.put({
        version: CACHE_VERSION,
        updatedAt: Date.now(),
        data,
    }, storageKey(userId))
);

const deleteCache = userId => runTransaction(
    'readwrite',
    store => store.delete(storageKey(userId))
);

export const activateApolloCacheForUser = async (cache, userId) => {
    const nextUserId = typeof userId === 'string' && userId.trim()
        ? userId.trim()
        : null;

    if (activeUserId === nextUserId) return;
    if (persistTimer) {
        clearTimeout(persistTimer);
        persistTimer = null;
    }

    activeUserId = nextUserId;
    cache.restore({});
    if (!nextUserId) return;

    try {
        const stored = await readCache(nextUserId);
        const cacheAge = Date.now() - stored?.updatedAt;
        if (
            activeUserId === nextUserId &&
            stored?.version === CACHE_VERSION &&
            Number.isFinite(cacheAge) &&
            cacheAge >= 0 &&
            cacheAge <= MAX_CACHE_AGE_MS &&
            stored.data &&
            typeof stored.data === 'object'
        ) {
            cache.restore(stored.data);
        } else if (stored && activeUserId === nextUserId) {
            // A later auth transition may have superseded this read. In that
            // case the older user's otherwise valid cache must not be deleted.
            await deleteCache(nextUserId);
        }
    } catch {
        // Privacy mode can disable IndexedDB. The memory cache remains usable.
    }
};

export const scheduleApolloCachePersist = cache => {
    if (!activeUserId || !canUseIndexedDb()) return;
    if (persistTimer) clearTimeout(persistTimer);

    const userIdAtScheduleTime = activeUserId;
    persistTimer = setTimeout(async () => {
        persistTimer = null;
        if (activeUserId !== userIdAtScheduleTime) return;

        try {
            const data = cache.extract();
            const serialized = JSON.stringify(data);
            if (serializedByteLength(serialized) > MAX_CACHE_BYTES) return;
            await writeCache(userIdAtScheduleTime, data);
        } catch {
            // Storage limits must never break the online application.
        }
    }, 500);
};

export const clearApolloCacheForUser = async (cache, userId) => {
    const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
    if (persistTimer) {
        clearTimeout(persistTimer);
        persistTimer = null;
    }
    if (normalizedUserId) {
        try {
            await deleteCache(normalizedUserId);
        } catch {
            // Best-effort cleanup when IndexedDB is unavailable.
        }
    }
    if (!normalizedUserId || activeUserId === normalizedUserId) {
        activeUserId = null;
        cache.restore({});
    }
};
